"use client";

import { m as motion } from "framer-motion";
import Link from "next/link";
import * as Tooltip from "@radix-ui/react-tooltip";
import {
  ArrowRight,
  ArrowUpRight,
  Gauge,
  Code2,
  ShoppingBag,
  Smartphone,
  Zap,
  Sparkles,
  Check,
  X,
  PhoneCall,
} from "lucide-react";
import { useLocale } from "next-intl";
import { ServiziHeroVisual } from "./ServiziHeroVisual";
import { ServiceFinder } from "./ServiceFinder";
import { ServiceMotif } from "./ServiceMotif";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { localizedRoutes } from "@/lib/routes/routes";
import { usePageVisibility } from "@/hooks/usePageVisibility";
import { trackEvent } from "@/utils/analytics";

const TECH_TERMS = [
  "FlutterFlow",
  "WooCommerce",
  "WordPress",
  "Shopify",
  "Next.js",
  "Three.js",
  "Lovable",
  "Bubble",
  "React",
  "Stripe",
  "PayPal",
  "v0",
];

function highlightTech(text: string) {
  const esc = TECH_TERMS.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${esc.join("|")})`, "g");
  return text.split(re).map((part, i) =>
    TECH_TERMS.includes(part) ? (
      <span key={i} className="font-medium text-primary">
        {part}
      </span>
    ) : (
      part
    )
  );
}

function ServiceDescription({ text }: { text: string }) {
  const sentences = text.split(/(?<=\.)\s+/).filter(Boolean);
  return (
    <div className="text-body text-muted-foreground mb-6 max-w-2xl space-y-2">
      {sentences.map((s, i) => (
        <p key={i}>{highlightTech(s)}</p>
      ))}
    </div>
  );
}

export type ServiceOverviewItem = {
  slug: string;
  href: string;
  name: string;
  text: string;
  price: string;
  time: string;
};

export function ServiziPageClient({ overview }: { overview: ServiceOverviewItem[] }) {
  const locale = useLocale();
  const { isVisible } = usePageVisibility();
  const isItalian = locale.startsWith("it");
  const routes = localizedRoutes[isItalian ? "it" : "en"];
  const bookingLink = `${routes.contacts}#tidycal`;
  const canShowProjects = isVisible("projects");
  const hrefOf = (slug: string) => overview.find((o) => o.slug === slug)?.href;
  const fit = isItalian
    ? [
        "Vuoi un partner diretto, non un call center",
        "Cerchi qualità che dura nel tempo, non la soluzione più veloce",
        "Sei disposto a un confronto rapido per fare le scelte giuste",
        "Vuoi il codice tuo, senza lock-in",
      ]
    : [
        "want a direct partner, not a call center",
        "want quality that lasts, not the fastest patch",
        "are open to a quick chat to make the right calls",
        "want to own your code, no lock-in",
      ];
  const noFit = isItalian
    ? [
        "Cerchi solo il prezzo più basso",
        "Vuoi tutto pronto in 3 giorni senza contenuti",
        "Cerchi un'agenzia con tanti referenti",
        "Vuoi delegare tutto senza mai confrontarti",
      ]
    : [
        "only want the lowest price",
        "want it all in 3 days with no content ready",
        "want an agency with many contacts",
        "want to delegate everything with zero involvement",
      ];

  const services = isItalian
    ? [
        {
          slug: "bug-fixing-ai-nocode",
          motif: "rebuild",
          icon: Code2,
          title: "Da progetto no-code a prodotto vero",
          description:
            "Hai costruito con Lovable, Bubble, v0 o FlutterFlow e ora non regge o non è sicuro. Lo prendo in mano e lo trasformo in un prodotto vero, su cui puoi davvero costruire.",
          outputs: [
            "Controllo completo del codice",
            "Mappa chiara dei problemi e delle priorità",
            "Messa in sicurezza di accessi e dati",
            "Riscrittura delle parti più deboli",
            "Una base pronta a crescere",
          ],
          benefits: [
            "Smetti di rincorrere bug che tornano",
            "Sai cosa hai davvero in mano",
            "Puoi aggiungere funzioni senza paura",
          ],
          stack: ["Lovable", "Bubble", "v0", "Next.js", "React"],
          delivery: "Si parte da una diagnosi, poi intervento a costo definito",
        },
        {
          slug: "siti-wordpress",
          motif: "target",
          icon: Gauge,
          title: "Siti WordPress su misura",
          description:
            "Siti WordPress veloci e fatti per te, niente template riciclati. Pensati per farti trovare su Google e per durare nel tempo.",
          outputs: [
            "Design su misura, non un tema comprato",
            "Pagine veloci anche da telefono",
            "Setup SEO di base pronto all'uso",
            "Modulo contatti con anti-spam",
            "Formazione per gestirlo da solo",
          ],
          benefits: [
            "Ti presenti meglio di chi usa template",
            "Carichi veloce, perdi meno visite",
            "Lo aggiorni senza chiamare nessuno",
          ],
          stack: ["WordPress", "WooCommerce", "PHP", "Core Web Vitals"],
          delivery: "3-6 settimane in base al numero di pagine",
        },
        {
          slug: "wordpress-performance",
          motif: "gauge",
          icon: Gauge,
          title: "Velocizzare un sito WordPress",
          description:
            "Il tuo sito WordPress ci mette troppo ad aprirsi e chi arriva se ne va. Trovo le cause vere, intervengo e ti consegno i numeri misurati prima e dopo.",
          outputs: [
            "Analisi con report e priorità",
            "Immagini, cache e plugin sistemati",
            "Trasloco su un hosting più veloce, se serve",
            "Velocità e qualità tecnica come piace a Google",
            "Numeri misurati prima e dopo",
          ],
          benefits: [
            "Perdi meno visite per l'attesa",
            "Il sito si apre veloce anche da telefono",
            "Sai esattamente cosa è stato fatto",
          ],
          stack: ["WordPress", "WooCommerce", "PageSpeed", "Cloudflare"],
          delivery: "analisi in 2-3 giorni, interventi in 1-3 settimane",
        },
        {
          slug: "ecommerce-woocommerce",
          motif: "cart",
          icon: ShoppingBag,
          title: "Ecommerce WooCommerce",
          description:
            "Costruisco o rifaccio il tuo negozio online su WooCommerce. Mi occupo della parte tecnica e di realizzazione: il sito che vende, non la strategia di vendita.",
          outputs: [
            "Negozio costruito o rifatto da zero",
            "Pagamenti con Stripe e PayPal",
            "Spedizioni e corrieri configurati",
            "Schede prodotto ordinate e chiare",
            "Pagine legali e GDPR a posto",
          ],
          benefits: [
            "Un negozio tuo, non un template generico",
            "Checkout semplice, meno carrelli persi",
            "Lo gestisci in autonomia dal pannello",
          ],
          stack: ["WooCommerce", "WordPress", "Stripe", "PHP"],
          delivery: "5-14 settimane in base al catalogo",
        },
        {
          slug: "sviluppo-custom-nextjs",
          motif: "code",
          icon: Code2,
          title: "Web app su misura",
          description:
            "Gestionali, portali clienti e dashboard quando WordPress non basta più. Lavoro a cicli settimanali e il codice resta tuo.",
          outputs: [
            "Le funzioni che servono al tuo lavoro",
            "Accessi utente e permessi",
            "Collegamento con gli strumenti che usi già",
            "Codice pulito su cui crescere",
            "Documentazione e passaggio di consegne",
          ],
          benefits: [
            "Uno strumento fatto sul tuo modo di lavorare",
            "Meno passaggi a mano tra un programma e l'altro",
            "Cresce con l'azienda, senza rifarlo",
          ],
          stack: ["Next.js", "React", "TypeScript", "PostgreSQL"],
          delivery: "da 3 settimane, a cicli settimanali",
        },
        {
          slug: "mvp-saas",
          motif: "target",
          icon: Code2,
          title: "MVP a prezzo fisso",
          description:
            "Hai un'idea di software e vuoi capire se qualcuno la paga. Costruisco la prima versione funzionante, con i pagamenti attivi, a prezzo fisso.",
          outputs: [
            "Le funzioni essenziali, niente di più",
            "Pagamenti attivi dal primo giorno",
            "Accessi utente e area riservata",
            "Codice tuo su GitHub",
            "Prezzo e tempi fissati prima di iniziare",
          ],
          benefits: [
            "Validi l'idea con clienti veri",
            "Non spendi su funzioni che nessuno usa",
            "Decidi i passi successivi su dati veri",
          ],
          stack: ["Next.js", "Stripe", "PostgreSQL", "TypeScript"],
          delivery: "4-6 settimane dal perimetro firmato",
        },
        {
          slug: "app-mobile",
          motif: "phone",
          icon: Smartphone,
          title: "App mobile",
          description:
            "Sviluppo app mobile da zero e mi occupo della manutenzione nel tempo. Già al lavoro su un'app per una startup.",
          outputs: [
            "App iOS e Android da un'unica base",
            "Funzioni costruite sulle tue esigenze",
            "Pubblicazione sugli store",
            "Aggiornamenti e correzioni nel tempo",
            "Codice tuo, niente lock-in",
          ],
          benefits: [
            "Un solo interlocutore per tutto",
            "Un'app che evolve con te",
            "Niente sorprese dopo il lancio",
          ],
          stack: ["Flutter", "Kotlin"],
          delivery: "Tempi definiti insieme dopo una prima diagnosi",
        },
        {
          slug: "automazioni-n8n-ai",
          motif: "nodes",
          icon: Zap,
          title: "Automazioni con l'AI",
          description:
            "Tolgo dalle tue giornate il lavoro manuale che si ripete. Costruisco processi che girano da soli e metto l'AI solo dove serve davvero.",
          outputs: [
            "Mappa dei processi da automatizzare",
            "Flussi di lavoro che girano da soli",
            "Integrazione con i tuoi strumenti",
            "AI dove fa la differenza",
            "Documentazione di come funziona",
          ],
          benefits: [
            "Recuperi ore di lavoro ogni settimana",
            "Meno errori da inserimenti manuali",
            "Le persone tornano a lavori di valore",
          ],
          stack: ["n8n", "OpenAI", "Claude", "Integrazioni"],
          delivery: "Da 1-2 settimane per il primo flusso",
        },
        {
          slug: "siti-3d-creative",
          motif: "cube",
          icon: Sparkles,
          title: "Lab creativo / 3D",
          description:
            "Esperienze 3D e configuratori web per prodotti, brand ed ecommerce. Quando ti serve qualcosa che le persone si ricordano.",
          outputs: [
            "Concept visivo e moodboard",
            "Modelli e animazioni 3D nel sito",
            "Configuratore di prodotto interattivo",
            "Integrazione fluida e leggera",
            "Demo e passaggio di consegne",
          ],
          benefits: [
            "Ti distingui da chi sembra tutto uguale",
            "Mostri il prodotto da ogni angolazione",
            "Le persone restano più a lungo",
          ],
          stack: ["Three.js", "Blender", "Spline", "GSAP"],
          delivery: "4-8 settimane in base al progetto",
          isAddon: false,
        },
        {
          slug: "manutenzione-evolutiva",
          motif: "cycle",
          icon: Zap,
          title: "Manutenzione evolutiva",
          description:
            "Il tuo sito o la tua app restano aggiornati, sicuri e veloci, e crescono quando serve. Funziona con l'abbonamento: una richiesta alla volta, canone fisso.",
          outputs: [
            "Aggiornamenti e correzioni di sicurezza",
            "Controllo che il sito resti veloce",
            "Piccole modifiche e nuove funzioni",
            "Correzione dei problemi quando emergono",
            "Un solo referente che conosce il progetto",
          ],
          benefits: [
            "Non devi più pensare alla parte tecnica",
            "Niente sorprese in fattura",
            "Il sito migliora invece di invecchiare",
          ],
          stack: ["WordPress", "WooCommerce", "Next.js", "Flutter"],
          delivery: "continuativo, con l'abbonamento sviluppatore",
        },
      ]
    : [
        {
          slug: "bug-fixing-ai-nocode",
          motif: "rebuild",
          icon: Code2,
          title: "From no-code project to a real product",
          description:
            "You built it with Lovable, Bubble, v0, or FlutterFlow and now it doesn't hold up or isn't secure. I take it over and turn it into a real product you can actually build on.",
          outputs: [
            "Full control of the code",
            "A clear map of issues and priorities",
            "Logins and data made secure",
            "The weakest parts rewritten",
            "A base ready to grow",
          ],
          benefits: [
            "Stop chasing bugs that keep coming back",
            "Know what you actually have",
            "Add features without fear",
          ],
          stack: ["Lovable", "Bubble", "v0", "Next.js", "React"],
          delivery: "Starts with a diagnosis, then work at a fixed cost",
        },
        {
          slug: "siti-wordpress",
          motif: "target",
          icon: Gauge,
          title: "Custom WordPress sites",
          description:
            "Fast WordPress sites built for you, no recycled templates. Made to get you found on Google and to last over time.",
          outputs: [
            "Custom design, not a bought theme",
            "Fast pages, also on mobile",
            "Base SEO setup ready to use",
            "Contact form with anti-spam",
            "Training so you can run it yourself",
          ],
          benefits: [
            "You look better than template sites",
            "Loads fast, loses fewer visits",
            "You update it without calling anyone",
          ],
          stack: ["WordPress", "WooCommerce", "PHP", "Core Web Vitals"],
          delivery: "3-6 weeks depending on the number of pages",
        },
        {
          slug: "wordpress-performance",
          motif: "gauge",
          icon: Gauge,
          title: "Speed up a WordPress site",
          description:
            "Your WordPress site takes too long to open and visitors leave. I find the real causes, fix them and hand you the numbers measured before and after.",
          outputs: [
            "Analysis with a report and priorities",
            "Images, cache and plugins sorted out",
            "Move to faster hosting, if needed",
            "Speed and technical quality the way Google likes it",
            "Numbers measured before and after",
          ],
          benefits: [
            "Fewer visits lost to waiting",
            "The site opens fast on phones too",
            "You know exactly what was done",
          ],
          stack: ["WordPress", "WooCommerce", "PageSpeed", "Cloudflare"],
          delivery: "analysis in 2-3 days, fixes in 1-3 weeks",
        },
        {
          slug: "ecommerce-woocommerce",
          motif: "cart",
          icon: ShoppingBag,
          title: "WooCommerce ecommerce",
          description:
            "I build or rebuild your online store on WooCommerce. I handle the technical build: the store that sells, not the sales strategy.",
          outputs: [
            "Store built or rebuilt from scratch",
            "Payments with Stripe and PayPal",
            "Shipping and couriers configured",
            "Clean, clear product pages",
            "Legal and GDPR pages in order",
          ],
          benefits: [
            "A store that's yours, not a generic template",
            "Simple checkout, fewer lost carts",
            "You run it yourself from the dashboard",
          ],
          stack: ["WooCommerce", "WordPress", "Stripe", "PHP"],
          delivery: "5-14 weeks depending on the catalog",
        },
        {
          slug: "sviluppo-custom-nextjs",
          motif: "code",
          icon: Code2,
          title: "Custom web apps",
          description:
            "Internal tools, client portals and dashboards when WordPress is no longer enough. I work in weekly cycles and the code stays yours.",
          outputs: [
            "The features your work needs",
            "User logins and permissions",
            "Connection with the tools you already use",
            "Clean code to grow on",
            "Documentation and handover",
          ],
          benefits: [
            "A tool built around how you work",
            "Fewer manual steps between programs",
            "It grows with the business, no rebuild",
          ],
          stack: ["Next.js", "React", "TypeScript", "PostgreSQL"],
          delivery: "from 3 weeks, in weekly cycles",
        },
        {
          slug: "mvp-saas",
          motif: "target",
          icon: Code2,
          title: "Fixed-price MVP",
          description:
            "You have a software idea and want to know if anyone will pay for it. I build the first working version, with payments on, at a fixed price.",
          outputs: [
            "The essential features, nothing more",
            "Payments working from day one",
            "User logins and a private area",
            "Your code on GitHub",
            "Price and timeline fixed before starting",
          ],
          benefits: [
            "Test the idea with real customers",
            "Don't spend on features nobody uses",
            "Decide next steps on real data",
          ],
          stack: ["Next.js", "Stripe", "PostgreSQL", "TypeScript"],
          delivery: "4-6 weeks from the signed scope",
        },
        {
          slug: "app-mobile",
          motif: "phone",
          icon: Smartphone,
          title: "Mobile apps",
          description:
            "I build mobile apps from scratch and handle maintenance over time. Already working on an app for a startup.",
          outputs: [
            "iOS and Android from one codebase",
            "Features built around your needs",
            "Publishing to the app stores",
            "Updates and fixes over time",
            "Your code, no lock-in",
          ],
          benefits: [
            "One person for everything",
            "An app that evolves with you",
            "No surprises after launch",
          ],
          stack: ["Flutter", "Kotlin"],
          delivery: "Timeline set together after a first diagnosis",
        },
        {
          slug: "automazioni-n8n-ai",
          motif: "nodes",
          icon: Zap,
          title: "Automations with AI",
          description:
            "I take the repetitive manual work out of your days. I build processes that run on their own and add AI only where it really helps.",
          outputs: [
            "A map of processes to automate",
            "Workflows that run on their own",
            "Integration with your tools",
            "AI where it makes a difference",
            "Documentation of how it works",
          ],
          benefits: [
            "Get back hours of work every week",
            "Fewer errors from manual entry",
            "People go back to valuable work",
          ],
          stack: ["n8n", "OpenAI", "Claude", "Integrazioni"],
          delivery: "From 1-2 weeks for the first workflow",
        },
        {
          slug: "siti-3d-creative",
          motif: "cube",
          icon: Sparkles,
          title: "Creative lab / 3D",
          description:
            "3D experiences and web configurators for products, brands, and ecommerce. For when you need something people remember.",
          outputs: [
            "Visual concept and moodboard",
            "3D models and animations in the site",
            "An interactive product configurator",
            "A smooth, lightweight integration",
            "Demo and handover",
          ],
          benefits: [
            "Stand out from sites that all look the same",
            "Show the product from every angle",
            "People stay longer",
          ],
          stack: ["Three.js", "Blender", "Spline", "GSAP"],
          delivery: "4-8 weeks depending on the project",
          isAddon: false,
        },
        {
          slug: "manutenzione-evolutiva",
          motif: "cycle",
          icon: Zap,
          title: "Ongoing maintenance",
          description:
            "Your site or app stays updated, secure and fast, and grows when needed. It runs on the subscription: one request at a time, fixed fee.",
          outputs: [
            "Updates and security fixes",
            "Checks that the site stays fast",
            "Small changes and new features",
            "Problems fixed as they come up",
            "One contact who knows the project",
          ],
          benefits: [
            "No more thinking about the technical side",
            "No surprises on the invoice",
            "The site improves instead of ageing",
          ],
          stack: ["WordPress", "WooCommerce", "Next.js", "Flutter"],
          delivery: "ongoing, with the developer subscription",
        },
      ];

  return (
    <Tooltip.Provider delayDuration={120}>
      <Layout>
        <section className="section-padding pt-8 md:pt-16 xl:min-h-[calc(100dvh-5.5rem)] xl:flex xl:flex-col xl:justify-center xl:py-0 relative overflow-hidden">
          <div className="absolute inset-0 pattern-dots" />
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />

          <div className="container-wide relative">
            <div className="grid xl:grid-cols-[1.12fr_0.88fr] gap-8 md:gap-10 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="max-w-3xl text-left"
              >
                <span className="font-mono text-label text-primary mb-4 block">
                  {isItalian ? "Servizi" : "Services"}
                </span>
                <h1 className="page-hero-title mb-6">
                  {isItalian ? "Servizi web e software per PMI" : "Web and software services for small businesses"}{" "}
                  <span className="text-shine">
                    {isItalian ? "a Bergamo" : "in Bergamo"}
                  </span>
                </h1>
                <p className="text-body-lg text-muted-foreground mb-4 leading-relaxed">
                  {isItalian ? (
                    <>
                      Siti WordPress, ecommerce, web app, app mobile e automazioni. Ogni servizio ha un{" "}
                      <span className="font-semibold text-primary">prezzo di partenza e tempi chiari</span>
                      , e parli sempre con me, dalla prima call alla consegna.
                    </>
                  ) : (
                    <>
                      WordPress sites, ecommerce, web apps, mobile apps and automations. Every service has a{" "}
                      <span className="font-semibold text-primary">starting price and a clear timeline</span>
                      , and you always talk to me, from the first call to delivery.
                    </>
                  )}
                </p>
                <p className="text-body text-muted-foreground/90 mb-8 max-w-2xl leading-relaxed">
                  {isItalian
                    ? "Lavoro con aziende e professionisti di Bergamo e provincia, e da remoto in tutta Italia."
                    : "I work with companies and professionals in Bergamo and its province, and remotely across Italy."}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-start">
                  <Button variant="hero" size="xl" asChild>
                    <Link
                      href={bookingLink}
                      onClick={() =>
                        trackEvent("book_call_click", {
                          tool: "tidycal",
                          location: "hero",
                        })
                      }
                    >
                      {isItalian ? "Prenota una call strategica" : "Book a strategy call"}
                      <PhoneCall className="w-4 h-4" />
                    </Link>
                  </Button>
                  <Button variant="outline" size="xl" className="rotating-border" asChild>
                    <Link
                      href={canShowProjects ? routes.projects : routes.method}
                      onClick={() =>
                        trackEvent("cta_click", {
                          cta_text: canShowProjects
                            ? isItalian
                              ? "Vedi i progetti"
                              : "View projects"
                            : isItalian
                              ? "Vedi il metodo"
                              : "See the method",
                          cta_section: "hero",
                        })
                      }
                    >
                      {canShowProjects
                        ? isItalian
                          ? "Vedi i progetti"
                          : "View projects"
                        : isItalian
                          ? "Vedi il metodo"
                          : "See the method"}
                      {canShowProjects ? (
                        <ArrowUpRight className="w-4 h-4" />
                      ) : (
                        <ArrowRight className="w-4 h-4" />
                      )}
                    </Link>
                  </Button>
                </div>
              </motion.div>

              <div className="flex justify-center items-center">
                <ServiziHeroVisual isItalian={isItalian} />
              </div>
            </div>
          </div>
        </section>

        {/* Tutti i servizi: prezzo "da" e tempi calcolati dagli stessi dati delle pagine servizio */}
        <section className="section-padding bg-surface/30" aria-labelledby="servizi-panoramica">
          <div className="container-wide">
            <div className="mb-8 max-w-2xl">
              <p className="font-mono text-label text-primary mb-3">
                {isItalian ? "Panoramica" : "Overview"}
              </p>
              <h2 id="servizi-panoramica" className="font-display text-h2 font-bold">
                {isItalian ? "Tutti i servizi a colpo d'occhio" : "All services at a glance"}
              </h2>
              <p className="mt-3 text-muted-foreground">
                {isItalian
                  ? "Prezzo di partenza e tempi tipici. Il prezzo esatto lo fisso per iscritto prima di iniziare."
                  : "Starting price and typical timeline. I fix the exact price in writing before starting."}
              </p>
            </div>
            <ul className="grid gap-3 md:grid-cols-2">
              {overview.map((o) => (
                <li key={o.slug}>
                  <Link
                    href={o.href}
                    onClick={() =>
                      trackEvent("cta_click", {
                        cta_text: `service_overview:${o.slug}`,
                        cta_section: "services_overview",
                      })
                    }
                    className="group flex h-full flex-col rounded-2xl border border-[hsl(var(--border))] bg-surface/60 p-5 transition-colors hover:border-primary/50"
                  >
                    <span className="flex items-start justify-between gap-3 font-semibold text-foreground transition-colors group-hover:text-primary">
                      {o.name}
                      <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
                    </span>
                    <span className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                      {o.text}
                    </span>
                    <span className="mt-4 flex flex-wrap gap-2 font-mono text-xs">
                      <span className="rounded-md bg-primary/10 px-2 py-1 text-primary">{o.price}</span>
                      <span className="rounded-md bg-surface px-2 py-1 text-muted-foreground">{o.time}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="section-padding">
          <div className="container-wide">
            <div className="space-y-12">
              {services.map((service, index) => (
                <motion.div
                  key={service.slug}
                  id={`servizio-${service.slug}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                  className="surface-card p-8 md:p-10 relative"
                  data-snap-anchor
                >
                  {service.isAddon && (
                    <Tooltip.Root>
                      <Tooltip.Trigger asChild>
                        <div className="absolute right-4 top-4 inline-flex cursor-help md:right-6 md:top-6">
                          <span className="px-3 py-1.5 rounded-lg bg-warm/10 text-warm text-xs font-mono font-medium">
                            Add-on
                          </span>
                        </div>
                      </Tooltip.Trigger>
                      <Tooltip.Portal>
                        <Tooltip.Content
                          side="top"
                          sideOffset={8}
                          className="px-3 py-2 rounded-md bg-muted text-xs text-yellow-500 shadow-lg"
                        >
                          {isItalian
                            ? "Modulo extra abbinabile ad altri servizi."
                            : "Extra module combinable with other services."}
                          <Tooltip.Arrow className="fill-muted" />
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    </Tooltip.Root>
                  )}

                  <div className="grid items-stretch gap-8 xl:grid-cols-[1.6fr_1fr]">
                    <div>
                      <div className="mb-4 flex items-center gap-4">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-glow">
                          <service.icon className="h-6 w-6 text-primary-foreground" />
                        </div>
                        <h2 className="font-display text-xl font-bold md:text-2xl">
                          {service.title}
                        </h2>
                      </div>
                      <ServiceDescription text={service.description} />

                      <div className="mb-6 grid gap-x-8 gap-y-6 sm:grid-cols-2">
                        <div>
                          <h3 className="font-display font-semibold text-sm mb-3 text-muted-foreground uppercase tracking-wider">
                            Output
                          </h3>
                          <ul className="space-y-2">
                            {service.outputs.map((output) => (
                              <li
                                key={output}
                                className="grid grid-cols-[16px_minmax(0,1fr)] items-start gap-2"
                              >
                                <Check className="mt-0.5 h-4 w-4 text-primary shrink-0" />
                                <span className="text-sm">{output}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h3 className="font-display font-semibold text-sm mb-3 text-primary uppercase tracking-wider">
                            {isItalian ? "Benefici" : "Benefits"}
                          </h3>
                          <ul className="space-y-2">
                            {service.benefits.map((benefit) => (
                              <li
                                key={benefit}
                                className="grid grid-cols-[16px_minmax(0,1fr)] items-start gap-2"
                              >
                                <ArrowRight className="mt-0.5 h-4 w-4 text-primary shrink-0" />
                                <span className="text-sm font-medium text-primary/90">
                                  {benefit}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        {service.stack.map((tech) => (
                          <span
                            key={tech}
                            className="px-3 py-1.5 rounded-lg bg-surface text-xs font-mono text-muted-foreground"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>

                      <p className="mt-5 text-sm text-muted-foreground">
                        <span className="font-mono uppercase tracking-wider text-primary/80">
                          {isItalian ? "Tempistiche" : "Timeline"}:
                        </span>{" "}
                        {service.delivery}
                      </p>

                    </div>

                    {/* Colonna destra: animazione in alto + CTA "Scopri il servizio"
                        allineata in basso (~riga Tempistiche), colore CTA del sito. */}
                    <div className="flex h-full flex-col items-center justify-between gap-6 py-1">
                      <div className="relative h-36 w-36 md:h-44 md:w-44 xl:h-48 xl:w-48">
                        <div className="pointer-events-none absolute inset-0 rounded-full bg-primary/10 blur-2xl" />
                        <div className="relative h-full w-full">
                          <ServiceMotif variant={service.motif} />
                        </div>
                      </div>
                      {hrefOf(service.slug) && (
                        <Link
                          href={hrefOf(service.slug)!}
                          onClick={() =>
                            trackEvent("cta_click", {
                              cta_text: `service_detail:${service.slug}`,
                              cta_section: "services_list",
                            })
                          }
                          className="group/cta inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-primary/50 bg-primary/[0.06] px-5 text-sm font-semibold text-primary transition-all duration-200 hover:border-primary hover:bg-primary/[0.12] hover:shadow-[0_0_24px_hsl(var(--primary)/0.18)]"
                        >
                          {isItalian ? "Scopri il servizio" : "Explore the service"}
                          <ArrowRight className="h-4 w-4 transition-transform group-hover/cta:translate-x-1" />
                        </Link>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-padding">
          <div className="container-wide">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-10 max-w-2xl"
            >
              <span className="mb-4 block font-mono text-label text-primary">
                {isItalian ? "Onestà prima di tutto" : "Honesty first"}
              </span>
              <h2 className="font-display text-h2 font-bold">
                {isItalian ? "Sono adatto a te?" : "Am I right for you?"}
              </h2>
            </motion.div>

            <div className="grid gap-6 md:grid-cols-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="surface-card p-6 md:p-8 svc-fit"
              >
                <h3 className="mb-5 font-mono text-sm font-semibold uppercase tracking-widest text-primary">
                  {isItalian ? "Adatto a te se" : "A fit if you"}
                </h3>
                <ul className="space-y-3">
                  {fit.map((t) => (
                    <li
                      key={t}
                      className="grid grid-cols-[18px_minmax(0,1fr)] items-start gap-3"
                    >
                      <Check className="mt-0.5 h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">{t}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.08 }}
                id="svc-non-adatto"
                className="surface-card p-6 md:p-8"
              >
                <h3 className="mb-5 font-mono text-sm font-semibold uppercase tracking-widest text-[hsl(var(--destructive))]">
                  {isItalian ? "Non adatto se" : "Not a fit if you"}
                </h3>
                <ul className="space-y-3">
                  {noFit.map((t) => (
                    <li
                      key={t}
                      className="grid grid-cols-[18px_minmax(0,1fr)] items-start gap-3"
                    >
                      <X className="mt-0.5 h-4 w-4 text-[hsl(var(--destructive))]" />
                      <span className="text-sm text-muted-foreground">{t}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="section-padding pb-24 md:pb-36" data-snap-label="CTA">
          <div className="container-wide">
            <ServiceFinder isItalian={isItalian} bookingLink={bookingLink} />
          </div>
        </section>
      </Layout>
    </Tooltip.Provider>
  );
}
